import React, { useState, useTransition } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from './Common/AuthField';
import { AuthLayout } from './Common/AuthLayout';
import { Button, Divider, Grid, GridRow, Header, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { authClient } from './authClient';
import { ForgotPasswordRequest } from '@app/api2/api';
import { useTranslation2 } from '../Contexts/TranslationContext2';
import { AuthLayoutRow } from './Common/AuthLayoutRow';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { BPButton } from '@/components/bp/bpbutton';

interface ForgotPasswordFormValues {
    email: string;
}

export const ForgotPassword: React.FC = () => {
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const { translations: t } = useTranslation2();

    const initialValues: ForgotPasswordFormValues = {
        email: ''
    };

    const validationSchema: yup.SchemaOf<ForgotPasswordFormValues> = yup.object().shape({
        email: yup.string()
            .email('Invalid email address')
            .required('Email is required')
    });

    const handleSubmit = async (values: ForgotPasswordFormValues) => {
        setError('');

        try {
            const request: ForgotPasswordRequest = {
                email: values.email
            };

            const result = await authClient.forgotPassword(request);

            if (result.success) {
                setIsSuccess(true);
            } else {
                setError('An error occurred while processing your request. Please try again.');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            setError('An error occurred while processing your request. Please try again.');
        }
    };

    if (isSuccess) {
        return (
            <AuthLayout header="Forgot your password?" description="Enter your email address and we'll send you a link to reset your password.">
                <AuthLayoutRow centered>
                    <Message success>
                        <Message.Header>{`Password reset link sent!`}</Message.Header>
                        <p>Please check your email for a link to reset your password. If it doesn't appear within a few minutes, check your spam folder.</p>
                    </Message>

                </AuthLayoutRow>
                <AuthLayoutRow centered>
                    <div>
                        <a href={buildUrl(AppRoutes.Login, {})}>Return to login</a>
                    </div>
                </AuthLayoutRow>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout header="Forgot your password?" description="Enter your email address and we'll send you a link to reset your password.">
            <AuthLayoutRow>
                <Segment>
                    {error && (
                        <Message error>
                            {error}
                        </Message>
                    )}

                    <BPForm
                        initialValues={initialValues}
                        vaildationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        hideButtons={true}
                    >
                        {(formProps) => (
                            <>
                                <AuthField
                                    name="email"
                                    label="Email"
                                    autoComplete="username"
                                    placeholder="name@example.com"
                                />

                                <Divider />

                                <BPButton
                                    className='bp-primary'
                                    type="submit"
                                    primary
                                    size="large"
                                    fluid
                                    disabled={formProps.isSubmitting}
                                    loading={formProps.isSubmitting}
                                >
                                    Submit
                                </BPButton>
                            </>
                        )}
                    </BPForm>
                </Segment>
            </AuthLayoutRow>
            <AuthLayoutRow>
                <p style={{ textAlign: 'center', marginTop: '1rem' }}>
                    Remember your password?{' '}
                    <a href={buildUrl(AppRoutes.Login, {})}>Log in here</a>
                </p>
            </AuthLayoutRow>
        </AuthLayout>
    );
};